// src/components/Inventory/uiStyles.js
// Theme-aware styles (Dark + Light mode)
// UPDATED: Dark mode now matches Material Management "neutral dark" look
// - Removes "blue glass" surfaces for cards/tables/timeline rows
// - Keeps blue as ACCENT (progress bars, subtle hovers) only

export const makeInventorySx = (theme) => {
  const mode = theme?.palette?.mode || "light";

  // MATERIAL-MANAGEMENT-LIKE DARK MODE (neutral dark surfaces, subtle borders)
  // Blue remains as an accent, not the main surface color.
  const dark = {
    // neutral MM-ish border + surfaces
    border: "rgba(255, 255, 255, 0.08)",
    borderStrong: "rgba(255, 255, 255, 0.12)",

    // neutral dark surface (MM style)
    surfaceA: "rgba(255, 255, 255, 0.04)",
    surfaceB: "rgba(255, 255, 255, 0.03)",

    // subtle hover (no bright blue wash)
    hover: "rgba(255, 255, 255, 0.03)",
    hover2: "rgba(255, 255, 255, 0.05)",

    // text like MM
    textMuted: "rgba(255, 255, 255, 0.72)",
    textMain: "rgba(255, 255, 255, 0.92)",
    headText: "rgba(255, 255, 255, 0.85)",

    // shadows like MM
    glow: "0 0 0 rgba(0,0,0,0)", // kill blue glow
    shadow: "0 12px 35px rgba(0, 0, 0, 0.45)",

    // headers: neutral dark (not blue)
    headerGrad:
      "linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)",

    // keep blue accents for progress only
    barGrad:
      "linear-gradient(90deg, rgba(77,163,255,0.95) 0%, rgba(107,182,255,0.92) 100%)",
    barBg: "rgba(77,163,255,0.14)",

    // zebra off / neutral (you said forget zebra stripes)
    zebraOdd: "transparent",
    zebraOddOverlay: "none",
  };

  // LIGHT MODE palette (keep as-is / soft)
  const light = {
    border: "rgba(0, 0, 0, 0.06)",
    borderStrong: "rgba(0, 0, 0, 0.10)",
    surfaceA: "rgba(255, 255, 255, 0.92)",
    surfaceB: "rgba(235, 246, 255, 0.92)",
    hover: "rgba(30, 90, 160, 0.06)",
    hover2: "rgba(30, 90, 160, 0.10)",
    textMuted: "rgba(10, 18, 28, 0.62)",
    textMain: "rgba(10, 18, 28, 0.88)",
    headText: "rgba(12, 42, 78, 0.78)",
    glow: "0 0 14px rgba(30, 90, 160, 0.12)",
    shadow: "0 10px 28px rgba(0,0,0,0.08)",
    headerGrad:
      "linear-gradient(135deg, rgba(30,90,160,0.10) 0%, rgba(77,163,255,0.08) 100%)",
    barGrad:
      "linear-gradient(90deg, rgba(30,90,160,0.88) 0%, rgba(77,163,255,0.85) 100%)",
    barBg: "rgba(30, 90, 160, 0.10)",
    zebraOdd: "transparent",
    zebraOddOverlay: "none",
  };

  const eco = mode === "dark" ? dark : light;

  const numberCellSx = {
    fontVariantNumeric: "tabular-nums",
    fontFeatureSettings: '"tnum" 1',
  };

  // Cards / panels (Allocation Summary, Donors panel, Reception Timeline, etc.)
  const cardSx = {
    borderRadius: 3,
    border: "1px solid",
    borderColor: eco.border,
    backgroundColor: eco.surfaceA,
    backgroundImage:
      mode === "dark"
        ? "none"
        : `linear-gradient(135deg, ${eco.surfaceA} 0%, ${eco.surfaceB} 100%)`,
    overflow: "hidden",
    boxShadow: eco.shadow,
  };

  // Card header rows ("Donors", "Material Reception Timeline", etc.)
  const cardHeaderSx = {
    px: 2,
    py: 1.25,
    borderBottom: "1px solid",
    borderColor: eco.border,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    backgroundImage: eco.headerGrad,
    "& .MuiTypography-root": {
      color: eco.textMain,
    },
    "& .MuiTypography-caption": {
      color: eco.textMuted,
    },
  };

  // Tables: make headers + rows dark/neutral (MM-like)
  const tableSx = {
    "& .MuiTableCell-head": {
      fontWeight: 900,
      fontSize: 12,
      letterSpacing: 0.35,
      textTransform: "uppercase",
      color: eco.headText,
      backgroundColor: mode === "dark" ? "rgba(255,255,255,0.03)" : eco.surfaceA,
      borderBottom: `2px solid ${eco.borderStrong}`,
    },

    "& .MuiTableCell-root": {
      borderBottom: `1px solid ${eco.border}`,
      color: eco.textMain,
      backgroundColor: mode === "dark" ? "rgba(255,255,255,0.02)" : "transparent",
    },

    "& .MuiTableRow-root": {
      transition: "background-color 120ms ease",
    },

    "& .MuiTableRow-root:hover td": {
      backgroundColor: eco.hover,
    },
  };

  // zebra disabled (kept for compatibility)
  const zebraBodySx = {
    "& tr:nth-of-type(odd) td": {
      backgroundColor: eco.zebraOdd,
      backgroundImage: eco.zebraOddOverlay,
    },
  };

  // Used for "row cards" (timeline items) — make them neutral dark too
  const softRowSx = {
    p: 1.25,
    borderRadius: 2,
    border: "1px solid",
    borderColor: eco.border,
    backgroundColor: eco.surfaceA,
    backgroundImage: mode === "dark" ? "none" : `linear-gradient(135deg, ${eco.surfaceA} 0%, ${eco.surfaceB} 100%)`,
    "&:hover": {
      backgroundColor: eco.hover2,
      boxShadow: mode === "dark" ? "none" : eco.glow,
    },
  };

  // Progress bars keep the blue accent
  const progressSx = {
    height: 10,
    borderRadius: 999,
    bgcolor: eco.barBg,
    "& .MuiLinearProgress-bar": {
      backgroundImage: eco.barGrad,
    },
  };

  // Chips inside inventory (timeline chips etc.) should be neutral in dark mode
  const chipSx = {
    fontWeight: 800,
    border: "1px solid",
    borderColor: eco.borderStrong,
    color: eco.textMain,
    backgroundColor:
      mode === "dark"
        ? "rgba(255,255,255,0.06)"
        : "rgba(30,90,160,0.06)",
  };

  const captionSx = { color: eco.textMuted };

  return {
    eco,
    cardSx,
    cardHeaderSx,
    tableSx,
    zebraBodySx,
    numberCellSx,
    softRowSx,
    progressSx,
    chipSx,
    captionSx,
  };
};
